/**
 * Client for the Verifieddit hub verification API, URL mode — Issue #75.
 *
 * The drop site's media is already hosted, so this spoke never re-uploads
 * bytes for verification: it posts the media's own hosted URL and the hub
 * fetches it server-side (https-only, public hosts) against the platform
 * trust list.
 *
 * Hub contract:
 *   POST {HUB}/api/v1/verify  {"url": "<hosted-media-url>"}
 *   200 → { validationState: "Valid"|"Invalid"|"Unsigned", claimVersion,
 *           signer: { issuer, alg, time, trusted }, manifestSummary,
 *           assertions: string[], errors: [{ code, explanation }] }
 *   422 → URL_UNFETCHABLE / SSRF_BLOCKED
 *   429 → rate limited
 */

export interface HubSigner {
  issuer: string | null
  alg: string | null
  time: string | null
  trusted: boolean
}

export interface HubVerifyError {
  code: string
  explanation: string
}

export interface HubVerifyResponse {
  validationState: 'Valid' | 'Invalid' | 'Unsigned'
  claimVersion?: number | string | null
  signer?: HubSigner | null
  manifestSummary?: Record<string, unknown> | null
  assertions?: string[]
  errors?: HubVerifyError[]
}

/** Drop-site verification status vocabulary (createVerification c2paStatus). */
export type DropC2paStatus = 'verified' | 'authentic' | 'invalid' | 'unsigned'

/** Shape consumed by App's applyServerResult (same contract as the legacy
 *  /api/drop/verify-server response). */
export interface ServerVerificationResult {
  hasC2PA: boolean
  status: 'trusted' | 'valid' | 'invalid' | 'unsigned'
  issuer?: string
  signedAt?: string
  claimGenerator?: string
}

// confirmed 2026-06-12, hub live: https://api.verifieddit.com is a PLACEHOLDER until the
// production hub verify API ships (Sanmarcsoft/verifieddit-www#424).
// Override per environment via VITE_HUB_VERIFY_BASE — do NOT flip
// production config before the hub is live.
export const HUB_VERIFY_BASE: string =
  (import.meta.env?.VITE_HUB_VERIFY_BASE as string | undefined) || 'https://api.verifieddit.com'

/**
 * Issue #75 state mapping:
 *   Valid + trusted signer   → verified
 *   Valid + untrusted signer → authentic
 *   Invalid                  → invalid
 *   Unsigned                 → unsigned
 */
export function mapHubStateToDropStatus(
  resp: Pick<HubVerifyResponse, 'validationState' | 'signer'>,
): DropC2paStatus {
  switch (resp.validationState) {
    case 'Valid':
      return resp.signer?.trusted === true ? 'verified' : 'authentic'
    case 'Invalid':
      return 'invalid'
    default:
      return 'unsigned'
  }
}

/** Adapt the hub contract to the legacy server-verification shape used by
 *  the SPA's post-upload result renderer. */
export function hubResponseToServerVerification(resp: HubVerifyResponse): ServerVerificationResult {
  const dropStatus = mapHubStateToDropStatus(resp)
  const statusMap: Record<DropC2paStatus, ServerVerificationResult['status']> = {
    verified: 'trusted',
    authentic: 'valid',
    invalid: 'invalid',
    unsigned: 'unsigned',
  }
  const claimGenerator = resp.manifestSummary?.claimGenerator
  return {
    hasC2PA: resp.validationState !== 'Unsigned',
    status: statusMap[dropStatus],
    issuer: resp.signer?.issuer ?? undefined,
    signedAt: resp.signer?.time ?? undefined,
    claimGenerator: typeof claimGenerator === 'string' ? claimGenerator : undefined,
  }
}

/**
 * Verify a hosted media URL via the hub. Returns the parsed contract on
 * success, null on any failure (422 unfetchable/SSRF, 429 rate limit,
 * network error, malformed body) — callers fall back to legacy paths.
 */
export async function verifyUrlViaHub(
  mediaUrl: string,
  hubBase: string = HUB_VERIFY_BASE,
): Promise<HubVerifyResponse | null> {
  try {
    const res = await fetch(`${hubBase}/api/v1/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: mediaUrl }),
    })

    if (res.status === 422) {
      const body = await res.json().catch(() => null) as { errors?: HubVerifyError[] } | null
      console.warn('[hub-verify] URL rejected by hub:', body?.errors?.[0]?.code || 'UNPROCESSABLE')
      return null
    }
    if (res.status === 429) {
      console.warn('[hub-verify] hub rate limit hit')
      return null
    }
    if (!res.ok) {
      console.warn(`[hub-verify] hub returned ${res.status}`)
      return null
    }

    const data = await res.json() as HubVerifyResponse
    if (!data || typeof data.validationState !== 'string') {
      console.warn('[hub-verify] malformed hub response')
      return null
    }
    return data
  } catch (err) {
    console.warn('[hub-verify] request failed:', err)
    return null
  }
}
